import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../assets/photos/hlthiqLogin.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isValidToken, setIsValidToken] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      validateToken(tokenFromUrl);
    } else {
      setValidating(false);
      setError("No reset token provided");
    }
  }, [searchParams]);

  const validateToken = async (token) => {
    try {
      const response = await fetch('/api/auth/validate-reset-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setIsValidToken(true);
      } else {
        setError(data.error || "Invalid or expired reset token");
      }
    } catch (error) {
      console.error("Token validation error:", error);
      setError("An error occurred while validating the token");
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage("Password has been reset successfully! Redirecting to login...");
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.message || "An error occurred while resetting your password");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="login-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-xl-5">
              <div className="login-card">
                <div className="login-form text-center">
                  <img
                    src={logo}
                    alt="HealthIQ Logo"
                    style={{ height: "60px" }}
                    className="mb-3"
                  />
                  <div className="mt-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Validating token...</span>
                    </div>
                    <p className="mt-3 text-muted">Validating reset token...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="login-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-xl-5">
              <div className="login-card">
                <div className="login-form">
                  <div className="text-center mb-4">
                    <img
                      src={logo}
                      alt="HealthIQ Logo"
                      style={{ height: "60px" }}
                      className="mb-3"
                    />
                    <h2
                      className="login-title gradient-text"
                      style={{
                        fontWeight: 700,
                        paddingTop: "0.5rem",
                        background: "linear-gradient(135deg, #4C1788, #8E5BCC)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        color: "transparent"
                      }}
                    >
                      Invalid Token
                    </h2>
                  </div>

                  <div className="alert alert-danger" role="alert">
                    {error || "The reset link is invalid or has expired."}
                  </div>

                  <div className="text-center mt-4">
                    <a href="/forgot-password" className="btn btn-primary">
                      Request New Reset Link
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-xl-5">
            <div className="login-card">
              <div className="login-form">
                <div className="text-center mb-4">
                  <img
                    src={logo}
                    alt="HealthIQ Logo"
                    style={{ height: "60px" }}
                    className="mb-3"
                  />
                  <h2
                    className="login-title gradient-text"
                    style={{
                      fontWeight: 700,
                      paddingTop: "0.5rem",
                      background: "linear-gradient(135deg, #4C1788, #8E5BCC)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      color: "transparent"
                    }}
                  >
                    Reset Password
                  </h2>
                  <p className="text-muted">Enter your new password</p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="alert alert-success" role="alert">
                    {message}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">
                      New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="newPassword"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                      required
                      minLength={8}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      required
                      minLength={8}
                    />
                  </div>

                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn"
                      disabled={loading}
                      style={{
                        background: "linear-gradient(135deg, #4C1788, #8E5BCC)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                      }}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Resetting...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-4">
                  <a href="/login" className="text-decoration-none">
                    <small className="text-muted">Back to Login</small>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;